import { ALL_STUDENTS, CERT_VALUES, ALL_CERTS_TYPE } from './certificates-constants';
import {
    DOWNLOAD_URL_ALL_STUDENTS_ALL_CERTS,
    DOWNLOAD_URL_ALL_STUDENTS_ALL_NEW_CERTS,
    generateUrlAllStudentsCertsForType,
    generateUrlStudentCertsForType,
    generateUrlStudentNewCerts,
    generateUrlStudentNewCertsForType,
    generateUrlAllStudentsNewCertsForType,
    ALL_PAST
} from './download-constants';
import { handleResourceError } from '../../helpers/resource-handlers';
import fetcher from '../../helpers/fetcher';
import {
    generateCertificateFilename,
    formatDateForFilename
} from '../../helpers/generate-certificate-filename';
import { SHOW_TOAST_MESSAGE } from '../../components/toast/toast-actions';
import { CLOSE_MODAL } from '../../components/modal/modal-actions';
import { getDownloadUrl, isUndefined } from '../../helpers/utils';
import fetcherZip from '../../helpers/fetcher-zip';

/**
 * Download Actions
 * Download certificates for one student or all students, by type (gold, silver, bronze)
 * or all in one
 */

export const ALL_CERTIFICATES_DOWNLOADED = 'ALL_CERTIFICATES_DOWNLOADED';
export const CERTIFICATES_DOWNLOADED_FOR_TYPE = 'CERTIFICATES_DOWNLOADED_FOR_TYPE';
export const DOWNLOAD_SERVICE_IS_ACTIVE = 'DOWNLOAD_SERVICE_IS_ACTIVE';

/**
 * Downloads pdf Past Certificates for all types (gold, silver, bronze) in one file
 * Use ALL_STUDENTS userProfileId to download for all students
 */
function downloadAllPastCerts({
    userProfileId,
    classroomId,
    productId,
    startDate,
    endDate,
    classroomName,
    studentName,
    downloadType
}) {
    let filename;
    if (userProfileId === ALL_STUDENTS) {
        filename = generateCertificateFilename({
            classroomName,
            ...getDatesForFileName(startDate, endDate)
        });
        return downloadAllClassPastCerts({
            paramData: {
                classroomId,
                productId,
                startDate,
                endDate
            },
            filename,
            downloadType
        });
    }
    filename = generateCertificateFilename({
        classroomName,
        studentName,
        ...getDatesForFileName(startDate, endDate)
    });
    return downloadAllStudentPastCertsForType({
        userProfileId,
        paramData: {
            classroomId,
            groupId: 0,
            productId,
            startDate,
            endDate,
            certificateType: CERT_VALUES[ALL_CERTS_TYPE]
        },
        actionType: ALL_CERTIFICATES_DOWNLOADED,
        filename,
        downloadType
    });
}

/**
 * Downloads pdf Past Certificates for a particular type (gold, silver, bronze)
 * Use ALL_STUDENTS userProfileId to download for all students
 */
function downloadPastCertsByType({
    certificateType,
    paramData: {
        userProfileId,
        classroomId,
        productId,
        startDate,
        endDate,
        classroomName,
        studentName
    },
    downloadType
}) {
    let filename;
    if (userProfileId === ALL_STUDENTS) {
        filename = generateCertificateFilename({
            classroomName,
            ...getDatesForFileName(startDate, endDate)
        });
        return downloadClassPastCertsForType({
            certificateType,
            paramData: {
                classroomId,
                productId,
                startDate,
                endDate
            },
            filename,
            downloadType
        });
    }
    filename = generateCertificateFilename({
        classroomName,
        studentName,
        ...getDatesForFileName(startDate, endDate)
    });
    return downloadAllStudentPastCertsForType({
        userProfileId,
        paramData: {
            classroomId,
            groupId: 0,
            productId,
            startDate,
            endDate,
            certificateType
        },
        actionType: CERTIFICATES_DOWNLOADED_FOR_TYPE,
        filename,
        downloadType
    });
}

function downloadServiceIsActive({ isActive, downloadType, dispatch }) {
    dispatch({
        type: DOWNLOAD_SERVICE_IS_ACTIVE,
        payload: {
            isActive,
            downloadType
        }
    });
}

function showFailDownloadToastMessage(dispatch, getState) {
    const message = getState().localeData['Toast - DownloadFail'];
    dispatch({
        type: SHOW_TOAST_MESSAGE,
        payload: {
            message,
            type: 'fail',
            hasClose: true
        }
    });
}

function closeModal(dispatch) {
    dispatch({ type: CLOSE_MODAL });
}

/*
* Downloads pdf Past Certificates for the entire class
*/
async function downloadAllClassPastCerts({
    paramData,
    filename
}) {
    const url = await getDownloadUrl({
        resourceUrl: DOWNLOAD_URL_ALL_STUDENTS_ALL_CERTS,
        paramData,
        dateFormat: 'MM-DD-YYYY'
    });

    return (dispatch, getState) => {
        const downloadType = ALL_PAST;
        downloadServiceIsActive({
            isActive: true,
            downloadType,
            dispatch
        });
        fetcher(url, `${filename}_PAST`)
            .then(() => {
                dispatch({ type: ALL_CERTIFICATES_DOWNLOADED });
                downloadServiceIsActive({
                    isActive: false,
                    downloadType,
                    dispatch
                });
            })
            .catch((err) => {
                handleResourceError(dispatch, err, 'downloadAllClassPastCerts');
                showFailDownloadToastMessage(dispatch, getState);
                downloadServiceIsActive({
                    isActive: false,
                    downloadType,
                    dispatch
                });
            });
    };
}

/*
* Downloads all pdf Past Certificates for a students + type
*/
async function downloadAllStudentPastCertsForType({
    userProfileId,
    paramData,
    actionType,
    filename,
    downloadType
}) {
    const url = await getDownloadUrl({
        resourceUrl: generateUrlStudentCertsForType(userProfileId),
        paramData
    });

    return (dispatch, getState) => {
        downloadServiceIsActive({
            isActive: true,
            downloadType,
            dispatch
        });
        fetcher(url, `${filename}_PAST`)
            .then(() => {
                dispatch({ type: actionType });
                downloadServiceIsActive({
                    isActive: false,
                    downloadType,
                    dispatch
                });
            })
            .catch((err) => {
                handleResourceError(dispatch, err, 'downloadAllStudentPastCertsForType');
                showFailDownloadToastMessage(dispatch, getState);
                downloadServiceIsActive({
                    isActive: false,
                    downloadType,
                    dispatch
                });
            });
    };
}

/**
 * Downloads pdf Past Certificates for the entire class by type
 */
async function downloadClassPastCertsForType({
    certificateType,
    paramData,
    filename,
    downloadType
}) {
    const url = await getDownloadUrl({
        resourceUrl: generateUrlAllStudentsCertsForType(certificateType),
        paramData
    });

    return (dispatch, getState) => {
        downloadServiceIsActive({
            isActive: true,
            downloadType,
            dispatch
        });
        fetcher(url, `${filename}_PAST`)
            .then(() => {
                dispatch({ type: CERTIFICATES_DOWNLOADED_FOR_TYPE });
                downloadServiceIsActive({
                    isActive: false,
                    downloadType,
                    dispatch
                });
            })
            .catch((err) => {
                handleResourceError(dispatch, err, 'downloadClassPastCertsForType');
                showFailDownloadToastMessage(dispatch, getState);
                downloadServiceIsActive({
                    isActive: false,
                    downloadType,
                    dispatch
                });
            });
    };
}


/**
 * Downloads pdf Certificates for all types (gold, silver, bronze) in one file
 *
 */
function downloadAllSchoolOrClassCerts({
    fullColor,
    classroomId,
    filterText,
    startDate,
    endDate,
    downloadType
}) {
    return downloadAllCerts({
        paramData: {
            classroomId,
            startDate,
            endDate,
            fullColor,
            nameFilter: isUndefined(filterText) ? '' : filterText
        },
        downloadType
    });
}

/**
 * Downloads pdf New Certificates for all types (gold, silver, bronze) in one file
 * Use ALL_STUDENTS userProfileId to download for all students
 */
function downloadAllNewCerts({
    classroomId,
    productId,
    startDate,
    endDate,
    userId = null,
    downloadType,
    classroomName,
    studentName,
    fullColor,
    isClassLevel,
    gemTypes
}) {
    let filename;

    if (isClassLevel) {
        filename = generateCertificateFilename({
            classroomName,
            ...getDatesForFileName(startDate, endDate)
        });
        return downloadAllClassNewCerts({
            paramData: {
                classroomId,
                productId,
                startDate,
                endDate,
                fullColor
            },
            gemTypes,
            filename,
            downloadType
        });
    }
    filename = generateCertificateFilename({
        classroomName,
        studentName,
        ...getDatesForFileName(startDate, endDate)
    });
    return downloadStudentNewCerts({
        userId,
        paramData: {
            classroomId,
            groupId: 0,
            productId,
            startDate,
            endDate,
            fullColor
        },
        gemTypes,
        actionType: ALL_CERTIFICATES_DOWNLOADED,
        filename,
        downloadType
    });
}

/**
 * Downloads pdf New Certificates for a particular type (bronze, silver, gold, etc....)
 * Use ALL_STUDENTS userProfileId to download for all students
 */
function downloadNewCertsByType({
    certificateRank,
    userProfileId,
    classroomId,
    productId,
    startDate,
    endDate,
    downloadType,
    userId,
    classroomName,
    studentName,
    fullColor,
    gemTypes
}) {
    let filename;
    if (userProfileId === ALL_STUDENTS) {
        filename = generateCertificateFilename({
            classroomName,
            ...getDatesForFileName(startDate, endDate)
        });
        return downloadClassNewCertsForType({
            certificateRank,
            paramData: {
                classroomId,
                productId,
                startDate,
                endDate,
                fullColor
            },
            gemTypes,
            filename,
            downloadType
        });
    }
    filename = generateCertificateFilename({
        classroomName,
        ...getDatesForFileName(startDate, endDate),
        studentName
    });
    return downloadStudentNewCertsForType({
        userId,
        certificateRank,
        paramData: {
            classroomId,
            groupId: 0,
            productId,
            startDate,
            endDate,
            fullColor
        },
        gemTypes,
        downloadType,
        filename
    });
}

/**
 * Downloads pdf New Certificates for the entire class
 */
async function downloadAllClassNewCerts({ paramData, filename, downloadType, gemTypes }) {
    let url = await getDownloadUrl({
        resourceUrl: DOWNLOAD_URL_ALL_STUDENTS_ALL_NEW_CERTS,
        paramData
    });

    if (gemTypes) {
        url = url.concat('&').concat(gemTypes.replace(/: /g, '=').replace(/, /g, '&'));
    }

    return (dispatch, getState) => {
        downloadServiceIsActive({
            isActive: true,
            downloadType,
            dispatch
        });
        return fetcher(url, `${filename}_NEW`)
            .then(() => {
                dispatch({ type: ALL_CERTIFICATES_DOWNLOADED });
                closeModal(dispatch);
                downloadServiceIsActive({
                    isActive: false,
                    downloadType,
                    dispatch
                });
            })
            .catch((err) => {
                handleResourceError(dispatch, err, 'downloadAllClassNewCerts');
                showFailDownloadToastMessage(dispatch, getState);
                closeModal(dispatch);
                downloadServiceIsActive({
                    isActive: false,
                    downloadType,
                    dispatch
                });
            });
    };
}

/*
 * Download all pdf New Certificates for a students
 */
async function downloadStudentNewCerts({
    userId,
    paramData,
    actionType,
    filename,
    downloadType,
    gemTypes
}) {
    let url = await getDownloadUrl({
        resourceUrl: generateUrlStudentNewCerts(userId),
        paramData
    });

    if (gemTypes) {
        url = url.concat('&').concat(gemTypes.replace(/: /g, '=').replace(/, /g, '&'));
    }

    return (dispatch, getState) => {
        downloadServiceIsActive({
            isActive: true,
            downloadType,
            dispatch
        });
        return fetcher(url, `${filename}_NEW`)
            .then(() => {
                dispatch({ type: actionType });
                closeModal(dispatch);
                downloadServiceIsActive({
                    isActive: false,
                    downloadType,
                    dispatch
                });
            })
            .catch((err) => {
                handleResourceError(dispatch, err, 'downloadStudentNewCerts');
                showFailDownloadToastMessage(dispatch, getState);
                closeModal(dispatch);
                downloadServiceIsActive({
                    isActive: false,
                    downloadType,
                    dispatch
                });
            });
    };
}

/*
* Downloads pdf New Certificates for the entire class by type
*
*/
async function downloadClassNewCertsForType({
    certificateRank,
    paramData,
    filename,
    downloadType,
    gemTypes
}) {
    let url = await getDownloadUrl({
        resourceUrl: generateUrlAllStudentsNewCertsForType(certificateRank),
        paramData
    });

    if (gemTypes) {
        url = url.concat('&').concat(gemTypes.replace(/: /g, '=').replace(/, /g, '&'));
    }

    return (dispatch, getState) => {
        downloadServiceIsActive({
            isActive: true,
            downloadType,
            dispatch
        });
        fetcher(url, `${filename}_NEW`)
            .then(() => {
                dispatch({ type: CERTIFICATES_DOWNLOADED_FOR_TYPE });
                closeModal(dispatch);
                downloadServiceIsActive({
                    isActive: false,
                    downloadType,
                    dispatch
                });
            })
            .catch((err) => {
                handleResourceError(dispatch, err, 'downloadClassNewCertsForType');
                showFailDownloadToastMessage(dispatch, getState);
                closeModal(dispatch);
                downloadServiceIsActive({
                    isActive: false,
                    downloadType,
                    dispatch
                });
            });
    };
}

/*
 * Download pdf New Certificates for a student by type
 *
 */
async function downloadStudentNewCertsForType({
    userId,
    certificateRank,
    downloadType,
    paramData,
    filename,
    gemTypes
}) {
    let url = await getDownloadUrl({
        resourceUrl: generateUrlStudentNewCertsForType({ userId, type: certificateRank }),
        paramData
    });

    if (gemTypes) {
        url = url.concat('&').concat(gemTypes.replace(/: /g, '=').replace(/, /g, '&'));
    }

    return (dispatch, getState) => {
        downloadServiceIsActive({
            isActive: true,
            downloadType,
            dispatch
        });
        fetcher(url, `${filename}_NEW`)
            .then(() => {
                dispatch({ type: CERTIFICATES_DOWNLOADED_FOR_TYPE });
                closeModal(dispatch);
                downloadServiceIsActive({
                    isActive: false,
                    downloadType,
                    dispatch
                });
            })
            .catch((err) => {
                handleResourceError(dispatch, err, 'downloadStudentNewCertsForType');
                showFailDownloadToastMessage(dispatch, getState);
                closeModal(dispatch);
                downloadServiceIsActive({
                    isActive: false,
                    downloadType,
                    dispatch
                });
            });
    };
}


/**
 * Downloads pdf of Certificates for the entire school/class based on type
 */
async function downloadAllCerts({ paramData, downloadType }) {
    const url = await getDownloadUrl({
        resourceUrl: DOWNLOAD_URL_ALL_STUDENTS_ALL_CERTS,
        paramData,
        dateFormat: 'MM-DD-YYYY'
    });
    return (dispatch, getState) => {
        downloadServiceIsActive({
            isActive: true,
            downloadType,
            dispatch
        });
        closeModal(dispatch);

        return fetcherZip(url)
            .then(() => {
                dispatch({ type: ALL_CERTIFICATES_DOWNLOADED });
                downloadServiceIsActive({
                    isActive: false,
                    downloadType,
                    dispatch
                });
            })
            .catch((err) => {
                handleResourceError(dispatch, err, 'downloadAllCerts');
                showFailDownloadToastMessage(dispatch, getState);
                closeModal(dispatch);
                downloadServiceIsActive({
                    isActive: false,
                    downloadType,
                    dispatch
                });
            });
    };
}


function getDatesForFileName(startDate, endDate) {
    return { startDate: formatDateForFilename(startDate), endDate: formatDateForFilename(endDate) };
}

export default {
    downloadAllPastCerts,
    downloadPastCertsByType,
    downloadAllNewCerts,
    downloadNewCertsByType,
    downloadAllSchoolOrClassCerts
};



// WEBPACK FOOTER //
// ./containers/certificates/download-certificates-actions.js